import type { IndexerSearchDocsQuery } from '@affine/graphql';
import { findQueryTermRanges } from '@streamlain/search-extract';

import { type DocsSearchAggregateItem } from './docs-search-aggregate';
import { markQueryTerms } from './query-terms';
import { isSearchSourceType, type SearchSourceType } from './search-source';
import { getDocsSearchLocationIdentity } from './source-navigation';

export function mapRemoteSearchDocsForQuickSearch(
  docs: IndexerSearchDocsQuery['workspace']['searchDocs'],
  query: string
): DocsSearchAggregateItem[] {
  return docs.flatMap((doc, index) => {
    const databaseTitle = doc.databaseTitle || undefined;
    const databaseRowLabel = doc.rowLabel || undefined;
    const databaseColumnName = doc.columnName || undefined;
    const highlight = doc.highlight || undefined;
    const searchVisibility =
      doc.visibility || doc.technicalSourceKind
        ? {
            visibility: doc.visibility || undefined,
            technicalSourceKind: doc.technicalSourceKind || undefined,
          }
        : {};
    const blockContent = doc.databaseId
      ? formatDatabaseSearchSnippet(
          {
            databaseTitle,
            databaseRowLabel,
            highlight,
          },
          query
        )
      : markQueryTerms(highlight, query);
    if (doc.databaseId && !blockContent) return [];

    return [
      {
        docId: doc.docId,
        title: doc.title,
        blockId: doc.databaseId || doc.blockId || undefined,
        blockContent,
        sourceType: remoteSourceType(doc),
        flavour: doc.flavour || undefined,
        blobId: doc.blobId || undefined,
        databaseId: doc.databaseId || undefined,
        databaseRowId: doc.rowId || undefined,
        databaseRowShardDocId: doc.rowShardDocId || undefined,
        databaseRowShardVersion: doc.rowShardVersion || undefined,
        databaseColumnId: doc.columnId || undefined,
        databaseTitle,
        databaseRowLabel,
        databaseColumnName,
        ...searchVisibility,
        // searchDocs answers in server order without a score.
        score: 0,
        rank: index,
      },
    ];
  });
}

/** The server names a block hit by its flavour; title and graph hits carry no block. */
function remoteSourceType(
  doc: IndexerSearchDocsQuery['workspace']['searchDocs'][number]
): SearchSourceType {
  if (doc.databaseId) return 'database';
  if (doc.sourceType && isSearchSourceType(doc.sourceType)) {
    return doc.sourceType;
  }
  return doc.blockId ? 'text' : 'doc';
}

function formatDatabaseSearchSnippet(
  input: {
    databaseTitle?: string;
    databaseRowLabel?: string;
    highlight?: string;
  },
  query: string
) {
  const valueMatches = hasQueryMatch(input.highlight, query);
  const rowLabelMatches = hasQueryMatch(input.databaseRowLabel, query);
  if (!valueMatches && !rowLabelMatches) return undefined;

  const rowLabel = rowLabelMatches
    ? markQueryTerms(input.databaseRowLabel, query)
    : input.databaseRowLabel;
  const context = [input.databaseTitle, rowLabel].filter(Boolean).join(' · ');
  if (!valueMatches) return context;

  const value = markQueryTerms(input.highlight, query);
  if (context && value) return `${context}: ${value}`;
  return value || context;
}

function hasQueryMatch(content: string | undefined, query: string) {
  if (!content) return false;
  return (
    content.includes('<b>') || findQueryTermRanges(content, query).length > 0
  );
}

export function mergeRemoteDocsSearchResults(
  aggregateResults: DocsSearchAggregateItem[],
  searchDocsResults: DocsSearchAggregateItem[]
): DocsSearchAggregateItem[] {
  const docIdsWithGranularAggregateHits = new Set(
    aggregateResults
      .filter(
        result =>
          result.sourceType !== 'doc' ||
          Boolean(result.blockId) ||
          Boolean(result.elementId) ||
          Boolean(result.databaseId)
      )
      .map(result => result.docId)
  );
  const seen = new Set<string>();
  const merged: DocsSearchAggregateItem[] = [];

  const filteredSearchDocsResults = searchDocsResults.filter(result => {
    const isGenericDocHit =
      result.sourceType === 'doc' &&
      !result.blockId &&
      !result.elementId &&
      !result.databaseId;
    return !(
      isGenericDocHit && docIdsWithGranularAggregateHits.has(result.docId)
    );
  });

  // searchDocs carries database provenance; prefer those database rows first,
  // then the aggregate hits, which also carry canvas element positions. The
  // remaining searchDocs rows add what only that endpoint found, e.g. an image
  // found by its OCR text, named by its own block flavour.
  const databaseSearchDocsResults = filteredSearchDocsResults.filter(
    result => result.sourceType === 'database'
  );
  const otherSearchDocsResults = filteredSearchDocsResults.filter(
    result => result.sourceType !== 'database'
  );

  for (const result of [
    ...databaseSearchDocsResults,
    ...aggregateResults,
    ...otherSearchDocsResults,
  ]) {
    const key = getDocsSearchLocationIdentity(result);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(result);
  }

  // searchDocs rows keep the server order, the aggregate hits follow by
  // score; the merged position becomes the rank of each server hit.
  return merged
    .sort(
      (a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity) || b.score - a.score
    )
    .map((result, rank) => ({ ...result, rank }));
}
